/* ==========================================================================
   AUREA — theme.js
   Vanilla JS, custom elements, no dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Utilities
  ------------------------------------------------------------------ */
  const formatMoney = (cents) => {
    const format = window.theme?.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  };

  const trapFocus = (container) => {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  };

  /* ------------------------------------------------------------------
     Scroll reveal (Intersection Observer)
  ------------------------------------------------------------------ */
  const initScrollReveal = () => {
    const targets = document.querySelectorAll('.scroll-reveal');
    if (!targets.length) return;
    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    targets.forEach((el) => observer.observe(el));
  };

  /* ------------------------------------------------------------------
     Sticky header transition
  ------------------------------------------------------------------ */
  const initHeader = () => {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    let lastKnown = 0;
    let ticking = false;
    const update = () => {
      wrapper.classList.toggle('is-scrolled', lastKnown > 24);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        lastKnown = window.scrollY;
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  };

  /* ------------------------------------------------------------------
     Subtle parallax on hero media
  ------------------------------------------------------------------ */
  const initParallax = () => {
    if (REDUCED_MOTION) return;
    const media = document.querySelectorAll('[data-parallax] img');
    if (!media.length) return;
    window.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => {
          media.forEach((img) => {
            const rect = img.closest('[data-parallax]').getBoundingClientRect();
            if (rect.bottom < 0 || rect.top > window.innerHeight) return;
            const offset = rect.top * -0.06;
            img.style.transform = `translateY(${offset}px) scale(1.08)`;
          });
        });
      },
      { passive: true }
    );
  };

  /* ------------------------------------------------------------------
     Mobile navigation
  ------------------------------------------------------------------ */
  const initMobileNav = () => {
    const toggle = document.querySelector('.header__menu-toggle');
    const nav = document.getElementById('MobileNav');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open);
      document.body.style.overflow = open ? 'hidden' : '';
    });
  };

  /* ------------------------------------------------------------------
     Cart drawer + cart API
  ------------------------------------------------------------------ */
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.overlay = this.querySelector('.cart-drawer__overlay');
      this.overlay?.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-cart-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onQuantityChange(e));
      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('cart:open', () => this.open());
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.releaseFocus = trapFocus(this.querySelector('.cart-drawer__panel'));
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.releaseFocus) this.releaseFocus();
    }

    async refresh() {
      const res = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('cart-drawer');
      if (fresh) {
        this.querySelector('.cart-drawer__panel').innerHTML =
          fresh.querySelector('.cart-drawer__panel').innerHTML;
        this.querySelectorAll('[data-cart-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.close())
        );
      }
      document.querySelectorAll('[data-cart-count]').forEach(async (el) => {
        const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
        el.textContent = cart.item_count;
      });
    }

    onClick(e) {
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.changeLine(remove.dataset.cartRemove, 0);
      }
      const upsell = e.target.closest('[data-upsell-add]');
      if (upsell) {
        e.preventDefault();
        this.addItem(upsell.dataset.upsellAdd, upsell);
      }
      const plus = e.target.closest('[data-qty-plus]');
      const minus = e.target.closest('[data-qty-minus]');
      if (plus || minus) {
        const input = (plus || minus).parentElement.querySelector('.quantity__input');
        const next = Math.max(0, Number(input.value) + (plus ? 1 : -1));
        this.changeLine(input.dataset.line, next);
      }
    }

    onQuantityChange(e) {
      const input = e.target.closest('.quantity__input[data-line]');
      if (input) this.changeLine(input.dataset.line, Math.max(0, Number(input.value)));
    }

    async changeLine(line, quantity) {
      try {
        await fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity })
        });
        this.refresh();
      } catch {
        alert(window.theme.strings.cartError);
      }
    }

    async addItem(variantId, button) {
      button?.classList.add('is-loading');
      try {
        await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        this.refresh();
      } finally {
        button?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Cart icon opens the drawer instead of navigating */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-toggle]');
    if (!link) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || document.body.classList.contains('template-cart')) return;
    e.preventDefault();
    drawer.open();
  });

  /* ------------------------------------------------------------------
     Product form (AJAX add to cart, selling plans, quantity)
  ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));

      this.querySelectorAll('[data-qty-plus], [data-qty-minus]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const input = this.querySelector('.quantity__input');
          const step = btn.hasAttribute('data-qty-plus') ? 1 : -1;
          input.value = Math.max(1, Number(input.value) + step);
        })
      );

      // Selling-plan radios drive the hidden selling_plan input.
      const planInput = this.querySelector('input[name="selling_plan"]');
      this.querySelectorAll('[data-purchase-option]').forEach((radio) =>
        radio.addEventListener('change', () => {
          if (!planInput) return;
          const freqSelect = this.querySelector(
            `[data-plan-frequency="${radio.value}"] select`
          );
          planInput.value =
            radio.dataset.sellingPlan === 'onetime'
              ? ''
              : freqSelect?.value || radio.dataset.sellingPlan || '';
        })
      );
      this.querySelectorAll('[data-plan-frequency] select').forEach((select) =>
        select.addEventListener('change', () => {
          if (planInput) planInput.value = select.value;
        })
      );
    }

    async onSubmit(e) {
      e.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.submitButton.classList.add('is-loading');
      const formData = new FormData(this.form);
      if (!formData.get('selling_plan')) formData.delete('selling_plan');
      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || window.theme.strings.cartError);
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.submitButton.classList.remove('is-loading');
      }
    }

    showError(message) {
      let el = this.querySelector('.product-form__error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'form-message form-message--error product-form__error';
        el.setAttribute('role', 'alert');
        this.form.append(el);
      }
      el.textContent = message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------
     Variant picker: updates price / availability / URL
  ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', () => this.onChange());
      const json = this.querySelector('[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input:checked, select')).map((el) => el.value);
    }

    onChange() {
      const match = this.variants.find((variant) =>
        variant.options.every((option, i) => option === this.selectedOptions[i])
      );
      const productInfo = this.closest('.product__info') || document;
      const idInput = productInfo.querySelector('input[name="id"]');
      const button = productInfo.querySelector('product-form [type="submit"]');
      const buttonText = button?.querySelector('span');
      const priceEl = productInfo.querySelector('[data-product-price]');

      if (!match) {
        button?.setAttribute('aria-disabled', 'true');
        if (buttonText) buttonText.textContent = window.theme.strings.unavailable;
        return;
      }
      if (idInput) idInput.value = match.id;
      if (priceEl) priceEl.textContent = formatMoney(match.price);
      if (match.available) {
        button?.removeAttribute('aria-disabled');
        if (buttonText) buttonText.textContent = window.theme.strings.addToCart;
      } else {
        button?.setAttribute('aria-disabled', 'true');
        if (buttonText) buttonText.textContent = window.theme.strings.soldOut;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     Product gallery: thumbnails, dots, zoom
  ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    constructor() {
      super();
      this.main = this.querySelector('.product__main-media');
      this.image = this.main?.querySelector('img');
      this.thumbs = this.querySelectorAll('.product__thumbnail');
      this.dots = this.querySelectorAll('.product__media-dot');

      this.thumbs.forEach((thumb, index) =>
        thumb.addEventListener('click', () => this.show(index))
      );
      this.dots.forEach((dot, index) =>
        dot.addEventListener('click', () => this.show(index))
      );
      this.main?.addEventListener('click', () => this.main.classList.toggle('is-zoomed'));
    }

    show(index) {
      const thumb = this.thumbs[index];
      if (!thumb || !this.image) return;
      const src = thumb.dataset.fullSrc;
      const srcset = thumb.dataset.fullSrcset;
      this.image.style.opacity = 0;
      const swap = () => {
        this.image.src = src;
        if (srcset) this.image.srcset = srcset;
        this.image.alt = thumb.dataset.alt || '';
        this.image.onload = () => (this.image.style.opacity = 1);
      };
      REDUCED_MOTION ? swap() : setTimeout(swap, 180);
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index));
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === index));
      this.main.classList.remove('is-zoomed');
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------
     Product tabs
  ------------------------------------------------------------------ */
  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((index + 1) % this.tabs.length);
          if (e.key === 'ArrowLeft') this.select((index - 1 + this.tabs.length) % this.tabs.length);
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index);
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => (panel.hidden = i !== index));
      this.tabs[index].focus();
    }
  }
  customElements.define('product-tabs', ProductTabs);

  /* ------------------------------------------------------------------
     Predictive search
  ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive-search');
      this.timer = null;
      this.input?.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 250);
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.results?.classList.add('hidden');
      });
    }
    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.classList.add('hidden');
        return;
      }
      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(
        query
      )}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
      const res = await fetch(url);
      if (!res.ok) return;
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = html.querySelector('#PredictiveSearchResults');
      if (fresh) {
        this.results.innerHTML = fresh.innerHTML;
        this.results.classList.remove('hidden');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     Quick add (product cards)
  ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-quick-add]');
    if (!button) return;
    e.preventDefault();
    button.classList.add('is-loading');
    try {
      await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.quickAdd), quantity: 1 }] })
      });
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));
    } finally {
      button.classList.remove('is-loading');
    }
  });

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initHeader();
    initParallax();
    initMobileNav();
  });
  document.addEventListener('shopify:section:load', initScrollReveal);
})();
